import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators';

import { TabsComponent } from '../tabs/tabs.component';
import { ngxLoadingAnimationTypes } from 'ngx-loading';
import * as _ from 'lodash';
import { dateAsString, dateTimeAsString } from '../../date.helper';
import { UserService } from '../../../user/user.service';
import { Inspection } from '../../interfaces';

const PrimaryWhite = '#ffffff';
const SecondaryGrey = '#ccc';

@Component({
  selector: 'app-list-view',
  templateUrl: './list-view.component.html',
  styleUrls: ['./list-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListViewComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  type = 'list';
  page;
  pageSize;
  searchString;
  currentFilters;
  preservefilters;
  private _orderBy;
  private _asc = true;
  private subscriptions: Subscription = new Subscription();
  private loadDebounced = new Subject<void>();
  private isNavigating = false;
  private navigationTimeout: any;
  private lastSavedParams: any = null;
  private isInitialLoad = true;
  public ngxLoadingAnimationTypes = ngxLoadingAnimationTypes;
  public primaryColour = PrimaryWhite;
  public secondaryColour = SecondaryGrey;

  @ViewChild('tabs', { static: true }) tabs: TabsComponent;

  get orderBy() {
    return this._orderBy;
  }
  set orderBy(value: string) {
    this._orderBy = value;
  }
  get asc() {
    return this._asc;
  }
  set asc(value: boolean) {
    this._asc = value;
  }
  get navigating() {
    return this.isNavigating;
  }

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    public userService: UserService,
  ) {}

  @Input() hideTabs = false;
  @Input() count = 0;
  @Input() data = [];
  @Input() column;
  @Input() canEdit;
  @Input() url;
  @Input() title;
  @Input() loading = false;
  @Input() actions: any;
  @Input() canSimpleSearch = true;
  @Input() canOrder = true;
  @Input() showAction = true;
  @Input() tabsName: any = { active: 'Active', archived: 'Archived' };
  @Input() SearchPlaceholder: 'Search';
  @Input() setInspectionSearch: boolean = false;
  @Input() tabType;
  @Input() CreateUrl;
  @Input() defaultOrderBy;
  @Input() defaultAsc = true;
  @Input() definedFilters;
  @Input() canAdvanceSearch = false;
  @Input() parms = {};
  @Input() tableName = '';

  @Output() action = new EventEmitter<void>();
  @Output() filters = new EventEmitter<any>();
  @Output() downloadReport = new EventEmitter<any>();
  @Output() onPageChangedevent = new EventEmitter<void>();

  async ngOnInit() {
    if (this.userService.cachedProfile && this.userService.cachedProfile.type == 'AdminUser') {
      this.preservefilters = await this.userService.getSetting(this.tableName);
    }
    if (this.preservefilters) await this.applyPreserveFilters();
    this.subscriptions.add(
      this.route.queryParams.subscribe(async params => {
        await this.updateParams(params);
        // Only save settings when filters actually change, not on pagination or initial load
        if (this.tableName && params && Object.keys(params).length > 0 && !this.isInitialLoad) {
          const updatedParams = { ...params };
          delete updatedParams.search;
          delete updatedParams.page; // Don't save page number

          // Check if anything meaningful changed (excluding page and pageSize)
          const paramsToCompare = { ...updatedParams };
          delete paramsToCompare.pageSize;

          const hasChanged =
            !this.lastSavedParams || JSON.stringify(this.lastSavedParams) !== JSON.stringify(paramsToCompare);

          if (hasChanged && Object.keys(paramsToCompare).length > 0) {
            this.lastSavedParams = { ...paramsToCompare };
            await this.userService.saveSetting(this.tableName, updatedParams).toPromise();
          }
        } else if (this.isInitialLoad) {
          // Initialize lastSavedParams after first load to prevent saving on initial navigation
          const updatedParams = { ...params };
          delete updatedParams.search;
          delete updatedParams.page;
          const paramsToCompare = { ...updatedParams };
          delete paramsToCompare.pageSize;
          this.lastSavedParams = paramsToCompare;
          this.isInitialLoad = false;
        }
        if (params && Object.keys(params).length > 0) await this.updateParams(params);
      }),
    );
    if (!this.CreateUrl) {
      this.CreateUrl = '/' + this.url + '/form/add';
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Reset navigation flag when data loads
    if (
      this.isNavigating &&
      (changes.data ||
        changes.count ||
        (changes.loading && changes.loading.previousValue === true && changes.loading.currentValue === false))
    ) {
      if (this.navigationTimeout) {
        clearTimeout(this.navigationTimeout);
        this.navigationTimeout = null;
      }
      this.isNavigating = false;
      this.cdr.detectChanges();
    }
  }

  async updateParams(params: any) {
    this.page = Number(params['page']) || 1;
    this.pageSize = Number(params['pageSize']) || 10;
    this.searchString = params['search'] || '';
    this.asc = params['asc'] ? params['asc'] === 'true' : this.defaultAsc;
    this.orderBy = params['orderBy'] || this.defaultOrderBy || this.column[0].orderBy;
    this.type = params['type'] || 'list';
    this.parms = params;
    this.filters.emit(this.getQueryParams());
  }

  applyPreserveFilters() {
    if (this.preservefilters) {
      // this.page = Number(this.preservefilters['page']) || 1;
      this.pageSize = Number(this.preservefilters['pageSize']) || 10;
      this.searchString = this.preservefilters['search'] || '';
      this.asc = this.preservefilters['asc'] ? this.preservefilters['asc'] === 'true' : this.defaultAsc;
      this.orderBy = this.preservefilters['orderBy'] || this.defaultOrderBy || this.column[0].orderBy;
      this.type = this.preservefilters['type'] || 'list';
      this.parms = this.preservefilters;
      this.setParams();
      this.filters.emit(this.getQueryParams());
    }
  }

  getQueryParams() {
    return {
      page: this.page,
      pageSize: this.pageSize,
      search: this.searchString,
      orderBy: this.orderBy,
      asc: this.asc,
      type: this.type,
      ...this.currentFilters,
    };
  }

  async setParams() {
    await this.router.navigate([], {
      queryParams: this.getQueryParams(),
    });

    try {
      if (!this.hideTabs) {
        await this.tabs.refreshCount();
      }
    } catch (error) {
      console.error('Error refreshing tabs count:', error);
    }

    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.subscriptions.add(
      this.loadDebounced
        .pipe(
          tap(),
          debounceTime(1000),
          tap(),
          switchMap(async () => {
            this.router.navigate([], {
              queryParams: {
                page: this.page,
                pageSize: this.pageSize,
                search: this.searchString,
                orderBy: this.orderBy,
                asc: this.asc,
                type: this.type,
              },
            });
          }),
          catchError(error => {
            console.error('Error in stream:', error);
            return EMPTY;
          }),
        )
        .subscribe(),
    );
  }

  async setSearch() {
    this.loadDebounced.next();
  }

  setSearchTerm(search) {
    this.searchString = search;
    this.loadDebounced.next();
  }

  getRowData(item: any, field: string): any {
    return _.get(item, field, null);
  }

  async onPageChanged(page) {
    if (this.isNavigating) {
      return;
    }

    if (this.navigationTimeout) {
      clearTimeout(this.navigationTimeout);
    }

    this.isNavigating = true;
    this.cdr.detectChanges();

    this.page = page;
    await this.setParams();

    // Safety fallback: reset after 2 seconds if data hasn't loaded yet
    this.navigationTimeout = setTimeout(() => {
      if (this.isNavigating) {
        this.isNavigating = false;
        this.cdr.detectChanges();
      }
      this.navigationTimeout = null;
    }, 3000);
  }

  setOrder(orderBy) {
    if (!orderBy) {
      return;
    }
    this.asc = this.orderBy === orderBy ? !this.asc : true;
    this.orderBy = orderBy;
    this.page = 1;
    this.setParams();
  }

  getOrderIcon(orderBy: string) {
    return {
      'fa-arrow-up': this.orderBy == orderBy && this.asc,
      'fa-arrow-down': this.orderBy == orderBy && !this.asc,
    };
  }

  open(ev, id: number) {
    if (this.url) {
      this.router.navigateByUrl(this.url + `/form/${id}`);
    }
  }

  // async executeFieldAction(action, event, item) {
  //   console.log('executeAction', action, item);
  //   await action.callback(event, item)();
  //   this.filters.emit(this.getQueryParams());
  //   this.tabs.refreshCount();
  // }

  async executeAction(action, item) {
    console.log('executeAction', action, item);
    await action.callback(item)();
    this.filters.emit(this.getQueryParams());
    this.tabs.refreshCount();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (this.navigationTimeout) {
      clearTimeout(this.navigationTimeout);
      this.navigationTimeout = null;
    }
  }

  getLink(item: any, column: any): string {
    let link = column.link;
    const matches = link.match(/{{\s*(.+?)\s*}}/g);

    if (matches) {
      matches.forEach(match => {
        const key = match.replace(/[{}]/g, '').trim();
        const value = this.getRowData(item, key);
        link = link.replace(match, value);
      });
    }
    return link;
  }

  getDateAndTime(date: Date) {
    return dateTimeAsString(date);
  }

  dateAsString(date: Date) {
    return dateAsString(date);
  }

  async filtersUpdated(filters: any) {
    this.loading = true;
    this.currentFilters = {};

    for (const filter of filters.filter(f => f.key !== null)) {
      this.currentFilters[filter.key] = filter.value;
      if (filter.operator) {
        this.currentFilters[filter.key + 'Operator'] = filter.operator;
      }
    }

    this.filters.emit({ ...this.getQueryParams(), ...this.currentFilters });
  }

  tabChanged(type: string) {
    this.currentFilters = {};
    this.filters.emit({ ...this.getQueryParams(), ...this.currentFilters });
  }

  createReport(ev, inspection: Inspection) {
    ev.stopPropagation();
    this.downloadReport.emit(inspection);
  }
}
